import {useState, useEffect} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {useTheme} from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import {useDispatch} from 'react-redux'

import {crudAPI} from '../../../../apis'
import {showAlert} from '../../../../actions'

import {DataGrid} from '@mui/x-data-grid'

import {
  Drawer,
  Button,
  Dialog,
  TextField,
  IconButton
} from '@mui/material'

import MenuIcon from '@mui/icons-material/Menu'
import CloseIcon from '@mui/icons-material/Close'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'

import ContactForm from './ContactForm' // your Formik HOC


const styles = {
  root: css` 
    height: 100%;
    display: flex;
    padding-top: 20px;

    h2 {
      font-weight: 600;
    }
  `,
  mobile: css`
    flex-direction: column;
    padding-top: 0px;
  `,
  header: css`
    padding: 10px 20px 0px 10px;
    display: flex;
    justify-content: space-between;
  `,
  sidebar: css`
    width: 40%;
    min-width: 280px;
    max-width: 320px;
    padding: 40px 20px;
    height: 100%;
    border-right: 1px solid #D3D3D3;

    h2 {
      padding-bottom: 20px;
    }
  `,
  groupListItem: css`
    padding: 10px 15px;
    border-radius: 8px;
    cursor: pointer;

    h5 {
      font-size: 1rem;
      font-weight: 500;
      margin: 0;
    }

    p {
      font-size: 0.8rem;
      color: #b4b4b4;
      font-weight: 500;
    }
  `,
  selected: css`
    background: #000;
    color: #fff;
  `,
  addContactButton: css`
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 0.9rem;
    font-weight: 500;
    border-radius: 19px;
    min-height: 38px;
    padding-right: 16px;
    border: 2px solid #000;
    cursor: pointer;
    background: #000;
    color: #fff;

    svg {
      color: #000;
    }
  `,
  icon: css`
    width: 36px;
    height: 36px;
    border-radius: 18px;
    background: #ececec;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  content: css`
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    padding: 40px;
    display: flex;
    flex-direction: column;

    @media (max-width: 800px) {
      padding: 20px;
    }
  `,
  contentHeader: css`
    width: 100%;
    display: flex;
    align-items: center;
    padding-bottom: 20px;
    justify-content: space-between;
  `,
  dataGridContainer: css`
    padding-top: 5px;
  `,
  info: css`
    margin-top: 30px;
    margin-bottom: 5px;
    font-size: 0.9rem;
    font-weight: 500;
    color: #aaaaaa;
  `
}



const ContactsManager = ({ close, onGroupCreated }) => {
  const theme = useTheme()
  const isSm  = useMediaQuery(theme.breakpoints.down('md'))

  const [openDrawer, setOpenDrawer] = useState(false)
  
  const dispatch = useDispatch()

  const [open, setOpen] = useState(false)
  const [groups, setGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)


  // Load groups (+contacts) on mount, ensure "General" exists, select it
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const { data: wrapper } = await crudAPI.index(
          '/contacts/contact_groups?include=contacts'
        )
        const groupsArray = wrapper.data         // [ { id, attributes, relationships }, … ]
        const included = wrapper.included || []  // [ { id, type:"contact", attributes }, … ]

        // build map of contactId → {id, …attributes}
        const contactMap = included
          .filter((i) => i.type === 'contact')
          .reduce((acc, i) => {
            acc[i.id] = { id: i.id, ...i.attributes }
            return acc
          }, {})

        // normalize groups into {id, name, contacts:[…]}
        const normalized = groupsArray.map((g) => {
          const rels = g.relationships?.contacts?.data || []
          const contacts = rels
            .map((r) => contactMap[r.id])
            .filter(Boolean)
          return {
            id: g.id,
            name: g.attributes.name,
            contacts
          }
        })

        // ensure "General" exists
        let general = normalized.find((g) => g.name === 'General')
        if (!general) {
          const {data: newWrapper} = await crudAPI.create(
            '/contacts/contact_groups',
            { name: 'General' }
          )
          const cg = newWrapper.data
          general = { id: cg.id, name: cg.attributes.name, contacts: [] }
          normalized.push(general)
        }

        setGroups(normalized)
        setSelectedGroup(general)
      } catch (err) {
        console.error('Failed loading groups:', err)
      }
    }
    loadGroups()
  }, [])



  // Add a contact into the selected group
  const handleSave = async ({name, email, phone, groups: picked}) => {
    // For each picked group name…
    await Promise.all(picked.map(async groupName => {
      // Find or create the group
      let group = groups.find(g => g.name === groupName)
      if (!group) {
        // POST /contact_groups
        const url = '/contacts/contact_groups'
        const { data: newGrpWrap } = await crudAPI.create(url, {name: groupName})

        const g = newGrpWrap.data
        group = { id: g.id, name: g.attributes.name, contacts: [] }
        onGroupCreated(group)
        setGroups(prev => [...prev, group])
      }

      // Now add the contact into *that* group
      const url = `/contacts/contact_groups/${group.id}/contacts`
      const { data: contactWrap } = await crudAPI.create(url, {name, email, phone})
      const c = contactWrap.data
      // Update local state for that specific group
      setGroups(prev =>
        prev.map(g => {
          if (g.id !== group.id) return g
          return {
            ...g,
            contacts: [
              ...g.contacts,
              { id: c.id, ...c.attributes }
            ]
          }
        })
      )
    }))

    // Close the dialog
    setOpen(false)
    dispatch(showAlert('Contact added', 'success'))
  }


  // Delete a contact from the selected group
  const handleDelete = async (contactId) => {
    if (!selectedGroup) return
    try {
      await crudAPI.destroy(
        `/contacts/contact_groups/${selectedGroup.id}/contacts/${contactId}`
      )
      setSelectedGroup((sg) => ({
        ...sg,
        contacts: sg.contacts.filter((c) => c.id !== contactId)
      }))
      dispatch(showAlert('Contact removed', 'success'))
    } catch (e) {
      dispatch(showAlert('Failed to remove contact', 'error'))
    }
  }



  const handleRowUpdate = async (updatedRow, originalRow) => {
    try {
      const url = `/contacts/contact_groups/${selectedGroup.id}/contacts/${updatedRow.id}`
      await crudAPI.update(url, {
        email: updatedRow.email,
        phone: updatedRow.phone
      })

      // Update local state
      setGroups(prevGroups =>
        prevGroups.map(group => {
          if (group.id !== selectedGroup.id) return group
          return {
            ...group,
            contacts: group.contacts.map(contact =>
              contact.id === updatedRow.id ? { ...contact, ...updatedRow } : contact
            )
          }
        })
      )

      dispatch(showAlert('Contact updated', 'success'))
      return updatedRow
    } catch (error) {
      dispatch(showAlert('Failed to update contact', 'error'))
      return originalRow // fallback to original if update fails
    }
  }



  // Column definitions for the DataGrid
  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      editable: true 
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      editable: true 
    },
    {
      field: 'phone',
      headerName: 'Phone',
      flex: 1,
      sortable: false,
      filterable: false,
      editable: true 
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Button 
          sx={{
            fontSize: '0.7rem', 
            textTransform: 'uppercase',
            fontWeight: '500'
          }}
          size='small' 
          variant='text' 
          color='error' 
          onClick={() => handleDelete(row.id)}
        >
          Remove
        </Button>
      )
    }
  ]


  const select = (group) => () => {
    setSelectedGroup(group)
  }

  function closeContactFrom() {
    setOpen(false)
  }


  return (
    <div css={[styles.root, isSm && styles.mobile]}>
      {!isSm && close &&
        <IconButton sx={{position: 'absolute', right: '5px', top: '5px'}} onClick={close}>
          <CloseIcon />
        </IconButton>
      }


      {isSm ? (
        <>
          <div css={styles.header}>
            <IconButton onClick={() => setOpenDrawer(true)}>
              <MenuIcon />
            </IconButton>

            {close && 
              <IconButton onClick={close}>
                <CloseIcon />
              </IconButton>
            }
          </div>

          <Drawer
            anchor='left'
            open={openDrawer}
            onClose={() => setOpenDrawer(false)}
            ModalProps={{keepMounted: true}}
            sx={{
              zIndex: `${theme.zIndex.modal + 1} !important`,
              '& .MuiDrawer-paper': {
                width: '40%',
                minWidth: '280px',
                maxWidth: '320px',
                boxSizing: 'border-box',
              }
            }}
          >
            <div css={styles.sidebar}>
              <h2>Groups</h2>
              {groups.map((group) => (
                <div
                  key={group.id}
                  onClick={select(group)} 
                  css={[
                    styles.groupListItem, 
                    group.name === selectedGroup.name && styles.selected
                  ]}>
                  <h5 key={group.id}>{group.name}</h5>
                  <p>{group.contacts?.length} members</p>
                </div>
              ))}
            </div>
          </Drawer>
        </>
        ) : (
          <section css={styles.sidebar}>
            <h2>Groups</h2>

            <div>
              {groups.map((group) => (
                <div
                  key={group.id}
                  onClick={select(group)} 
                  css={[
                    styles.groupListItem, 
                    group.name === selectedGroup.name && styles.selected
                  ]}>
                  <h5 key={group.id}>{group.name}</h5>
                  <p>{group.contacts?.length} members</p>
                </div>
              ))}
            </div>
          </section>
        )
      }


      {/* Header + Add Contact */}
      <section css={styles.content}>
        <div css={styles.contentHeader}>
          <h2 style={{fontWeight: 500}}>
            {selectedGroup
              ? `${selectedGroup.contacts.length} contacts in ${selectedGroup.name}`
              : 'Loading…'
            }
          </h2>


          <div 
            css={styles.addContactButton} 
            onClick={() => setOpen(true)} 
            disabled={!selectedGroup}
          >
            <div css={styles.icon}>
              <PersonAddAlt1Icon fontSize='small' />
            </div>
            Add Contact
          </div>
        </div>


        <TextField placeholder='Search' variant='filled' fullWidth />

        <p css={styles.info}>
          Double click a cell in the table below to edit value
        </p>

        {/* Contacts DataGrid */}
        <div css={styles.dataGridContainer}>
          <DataGrid
            rows={selectedGroup?.contacts || []}
            columns={columns}
            pageSize={25}
            pageSizeOptions={[10, 25, 50, 100]}
            processRowUpdate={handleRowUpdate}
            experimentalFeatures={{newEditingApi: true}}
            disableSelectionOnClick
            sx={{
              width: '100%',
              border: 0,
              borderRadius: '8px',
                 
              // Cell
              '& .MuiDataGrid-cell': {
                fontSize: '0.93rem',
                borderBottom: 'none',
                borderTop: 'none',
                cursor: 'pointer'
              },

              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
              // Row
              '& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within': {
                outline: 'none',
              },
              // Header
              '& .MuiDataGrid-columnHeader': {
                background: 'rgba(255,241,209,0.4)',
                textTransform: 'uppercase',
                border: 'none',
                fontSize: '0.8rem',
              },
              
              '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                outline: 'none',
              },

              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
            }}
          />
        </div>
      </section>

      {/* Add Contact Dialog */}
      <Dialog open={open} onClose={closeContactFrom}>
        <ContactForm onSave={handleSave} close={closeContactFrom} />
      </Dialog>
    </div>
  )
}


export default ContactsManager


